import React, { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthContext";
import { debugInfo, debugError } from "../utils/debug";
import SettingsModal from "./SettingsModal";
import FileUploadModal from "./FileUploadModal";
import { parseAllCrdFiles, getCrdDocument } from "../services/crdApi";

const Dashboard = ({ onLogout }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeContracts: 0,
    pendingRequests: 0,
    completedTasks: 0,
  });
  const [loading, setLoading] = useState(false); // Не загружаем данные автоматически
  const [refreshing, setRefreshing] = useState(false); // Локальный индикатор обновления таблицы
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [dataSource, setDataSource] = useState(''); // 'cache', 'fresh', или пустая строка

  // Состояние фильтров - по умолчанию текущий год
  const getCurrentYearDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    return {
      from: `${year}-01-01`,
      to: `${year}-12-31`
    };
  };
  
  const yearDates = getCurrentYearDates();
  const [dateFrom, setDateFrom] = useState(yearDates.from);
  const [dateTo, setDateTo] = useState(yearDates.to);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  // Данные таблицы - изначально пустая таблица
  const [tableRows, setTableRows] = useState([]);
  
  // Флаг для предотвращения множественных загрузок
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Состояние для модального окна с данными CRD
  const [isCrdModalOpen, setIsCrdModalOpen] = useState(false);
  const [selectedCrdData, setSelectedCrdData] = useState(null);

    // Состояние выделения строк
    const [selectedRows, setSelectedRows] = useState([]);

    // Обработчик выделения строки
    const handleSelectRow = (id) => {
      setSelectedRows((prev) =>
        prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
      );
    };

    // Обработчик выделения всех
    const handleSelectAll = (e) => {
      if (e.target.checked) {
        setSelectedRows(filteredTableRows.map((row) => row.id));
      } else {
        setSelectedRows([]);
      }
    };

    // Фильтрация и сортировка данных таблицы
    const filteredAndSortedTableRows = tableRows
      .filter((row) => {
        if (!searchQuery.trim()) return true;
        
        const query = searchQuery.toLowerCase();
        // Преобразуем в строку перед вызовом toLowerCase (поля могут быть числами)
        const number = String(row.number || '').toLowerCase();
        const contractor = String(row.contractor || '').toLowerCase();
        
        return number.includes(query) || contractor.includes(query);
      })
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Особая обработка для даты
        if (sortConfig.key === 'date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        
        // Особая обработка для суммы
        if (sortConfig.key === 'amount') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }
        
        // Сравнение
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });

    // Для обратной совместимости оставляем старое название
    const filteredTableRows = filteredAndSortedTableRows;

    // Обработчик сортировки
    const handleSort = (key) => {
      setSortConfig(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      }));
    };

  // Обработчик фильтрации - вызывает синхронизацию SBIS
  const handleFilter = async () => {
    debugInfo("Dashboard", "Filtering data", { dateFrom, dateTo });
    // Загружаем данные с выбранными датами
    setRefreshing(true);
    setDataSource('fresh');
    await loadInvoiceData();
    setRefreshing(false);
  };

  // Обработчик создания документа в СБИС
  const handleCreateSbisDocument = async (rowData) => {
    debugInfo("Dashboard", "Creating SBIS document", { rowData });
    
    try {
      // Проверяем наличие необходимых данных
      if (!rowData || !rowData.ukey) {
        alert('Ошибка: отсутствует UKEY документа');
        return;
      }
      
      if (!rowData.tableName) {
        alert('Ошибка: отсутствует информация о таблице CRD');
        return;
      }
      
      if (!rowData.yearMonth) {
        alert('Ошибка: отсутствует информация о периоде документа');
        return;
      }
      
      // Подтверждение создания
      const confirmed = window.confirm(
        `Создать документ в СБИС?\n\n` +
        `Номер: ${rowData.number}\n` +
        `Дата: ${rowData.date}\n` +
        `Контрагент: ${rowData.contractor}\n` +
        `Сумма: ${rowData.amount}\n\n` +
        `Будет создан документ "Реализация" с XML вложением (УПД).`
      );
      
      if (!confirmed) {
        return;
      }
      
      // Показываем индикатор загрузки
      setSelectedCrdData({
        ...rowData,
        creating: true
      });
      
      // Импортируем API
      const { createSbisDocument } = await import("../services/sbisApi");
      
      // Создаем документ
      const result = await createSbisDocument(
        user.email,
        rowData.tableName,
        rowData.ukey,
        rowData.yearMonth,
        'invoice' // По умолчанию УПД
      );
      
      if (result.success) {
        debugInfo("Dashboard", "SBIS document created successfully", result);
        
        // Обновляем данные модального окна
        setSelectedCrdData({
          ...rowData,
          creating: false,
          sbisLink: result.document.link,
          sbisCreated: true
        });
        
        alert(
          `✅ Документ успешно создан в СБИС!\n\n` +
          `ID: ${result.document.id}\n` +
          `Номер: ${result.document.number}\n` +
          `Дата: ${result.document.date}\n\n` +
          `Документ откроется в новой вкладке.`
        );
        
        // Открываем документ в новой вкладке
        window.open(result.document.link, '_blank');
        
        // Обновляем таблицу
        handleRefresh();
      } else {
        throw new Error(result.error || 'Неизвестная ошибка');
      }
      
    } catch (error) {
      debugError("Dashboard", "Error creating SBIS document", error);
      
      setSelectedCrdData({
        ...rowData,
        creating: false,
        error: error.message
      });
      
      alert(`❌ Ошибка создания документа в СБИС:\n\n${error.message}`);
    }
  };

  // Обработчик клика по галочке создания СФ в СБИС
  const handleCreateInvoiceClick = async (row) => {
    debugInfo("Dashboard", "Opening CRD modal for invoice", { row });
    
    try {
      // Показываем индикатор загрузки
      setSelectedCrdData({ loading: true, ...row });
      setIsCrdModalOpen(true);
      
      // Определяем название таблицы на основе типа документа
      // По умолчанию используем СЧЕТФАКТ
      let tableName = 'СЧЕТФАКТ';
      
      // Пытаемся определить правильную таблицу из данных строки
      if (row.description && row.description.includes('получен')) {
        tableName = 'СЧЕТФАКТПОСТ';
      } else if (row.type === 'Входящая') {
        tableName = 'СЧЕТФАКТПОСТ';
      }
      
      // Извлекаем UKEY из данных строки
      let documentId = row.ukey;
      
      // Для объединённых документов UKEY может быть в duplicates
      if (!documentId && row.duplicates && Array.isArray(row.duplicates)) {
        const parsedDup = row.duplicates.find(d => d.ukey && (d.source === 'parsed' || d.source === 'crd'));
        if (parsedDup) {
          documentId = parsedDup.ukey;
          debugInfo("Dashboard", "Found UKEY in duplicates", { ukey: documentId });
        }
      }
      
      // Проверяем, есть ли данные из Turbo (CRD)
      const hasParsedData = row.hasParsed || (row.source === 'parsed') || (row.source === 'crd');
      
      // Если нет UKEY или нет данных из Parsed, это документ только из SBIS
      if (!documentId || !hasParsedData) {
        debugInfo("Dashboard", "No parsed data found", { 
          row, 
          documentId, 
          hasParsedData,
          ukey: row.ukey,
          hasParsed: row.hasParsed,
          source: row.source,
          duplicates: row.duplicates
        });
        setSelectedCrdData({
          ...row,
          error: 'Этот документ доступен только в СБИС. Данные из CRD файлов (Турбо) не найдены.',
          loading: false
        });
        return;
      }
      
      // Извлекаем год-месяц из даты для оптимизации поиска
      let yearMonth = null;
      if (row.date) {
        const dateParts = row.date.split('-');
        if (dateParts.length >= 2) {
          yearMonth = `${dateParts[0]}-${dateParts[1]}`;
        }
      }
      
      debugInfo("Dashboard", "Fetching CRD document", { 
        tableName, 
        documentId, 
        yearMonth,
        rowData: row
      });
      
      // Загружаем полные данные документа из CRD
      const result = await getCrdDocument(user.email, tableName, documentId, yearMonth);
      
      if (result.success && result.document) {
        debugInfo("Dashboard", "CRD document loaded successfully", result);
        
        // Обновляем модальное окно с полными данными
        setSelectedCrdData({
          ...row,
          crdData: result.document,
          tableName: result.tableName,
          yearMonth: result.yearMonth,
          loading: false
        });
      } else {
        throw new Error(result.error || 'Failed to load CRD document');
      }
      
    } catch (error) {
      debugError("Dashboard", "Error loading CRD document", error);
      
      // Показываем ошибку в модальном окне
      setSelectedCrdData({
        ...row,
        error: error.message || 'Не удалось загрузить данные из CRD',
        loading: false
      });
    }
  };

  // Загрузка свежих данных таблицы
  const loadInvoiceData = async () => {
    try {
      debugInfo("Dashboard", "Loading invoice data", { dateFrom, dateTo });
      
      // Импортируем API сервис
      const { getSbisInvoiceTable } = await import("../services/sbisApi");
      
      // Передаем даты в формате YYYY-MM-DD (backend ожидает именно этот формат)
      const result = await getSbisInvoiceTable(user.email, {
        dateFrom: dateFrom,
        dateTo: dateTo
      });
      
      // Проверяем, есть ли данные из CRD файлов
      if (result.success && result.data) {
        const hasCrdData = result.data.some(invoice => invoice.source === 'crd');
        
        // Если нет данных из CRD, запускаем автоматический парсинг
        if (!hasCrdData) {
          console.log("⚠️ CRD данные не найдены, запускаем автоматический парсинг...");
          try {
            // parseAllCrdFiles(userEmail, format, force)
            const parseResult = await parseAllCrdFiles(user.email, 'json', false);
            const stats = parseResult.statistics || parseResult.summary || {};
            if (parseResult.success && (stats.tablesExtracted > 0 || stats.totalTables > 0)) {
              console.log("✅ Автоматический парсинг CRD завершен успешно");
              // Перезагружаем данные после парсинга
              const updatedResult = await getSbisInvoiceTable(user.email, {
                dateFrom: dateFrom,
                dateTo: dateTo
              });
              if (updatedResult.success && updatedResult.data) {
                processInvoiceData(updatedResult.data);
              }
              return;
            }
          } catch (parseError) {
            console.warn("⚠️ Автоматический парсинг CRD не удался:", parseError.message);
            // Продолжаем с данными которые есть
          }
        }
        
        processInvoiceData(result.data);
      }
    } catch (error) {
      debugError("Dashboard", "Error loading invoice data", error);
    }
  };

  // Вспомогательная функция для обработки данных счет-фактур
  const processInvoiceData = (invoices) => {
    console.log('🔄 processInvoiceData вызвана, обрабатывается записей:', invoices.length);
    
    // Преобразуем данные в формат таблицы
    const invoiceRows = invoices.map((invoice, index) => {
      // Извлекаем контрагента с учетом разных форматов данных
      let contractorName = 'Не указан';
      
      if (invoice.counterparty?.name) {
        // Для SBIS данных: counterparty.name может быть объектом
        if (typeof invoice.counterparty.name === 'string') {
          contractorName = invoice.counterparty.name;
        } else {
          // Для SBIS API: counterparty.name.СвЮЛ.Название или .Название
          contractorName = invoice.counterparty.name?.СвЮЛ?.Название || 
                         invoice.counterparty.name?.Название || 
                         'Не указан';
        }
      }
      
      return {
        id: invoice.id || index,
        ukey: invoice.ukey || null, // Добавляем UKEY для поиска в CRD
        date: invoice.date,
        contractor: contractorName,
        amount: invoice.amount || 0,
        type: invoice.direction_text,
        status: invoice.status === 'unknown' ? 'Обработан' : invoice.status,
        number: invoice.number,
        description: invoice.description,
        source: invoice.source || 'sbis', // Добавляем источник данных
        sbisLink: invoice.sbisLink || '', // Добавляем ссылку на документ в СБИС
        processed: invoice.processed || false, // Флаг проведённого документа в СБИС
        hasSbis: invoice.hasSbis, // Флаг наличия данных из СБИС
        hasParsed: invoice.hasParsed, // Флаг наличия данных из Турбо
        sources: invoice.sources // Массив источников для объединённых записей
      };
    });
    
    setTableRows(invoiceRows);
    setDataSource('fresh');
    debugInfo("Dashboard", "Invoice data loaded successfully", { count: invoiceRows.length });
    
    // Сохраняем таблицу в S3 для последующего отображения
    saveDashboardTableToS3(invoiceRows);
  };

  // Сохранение таблицы Dashboard в S3
  const saveDashboardTableToS3 = async (tableData) => {
    try {
      console.log("💾 Сохранение таблицы Dashboard в S3...");
      debugInfo("Dashboard", "Saving table to S3", { rowCount: tableData.length });
      
      // Импортируем S3 сервис
      const { uploadToS3 } = await import("../services/s3Service");
      
      // Формируем данные для сохранения
      const dashboardData = {
        tableRows: tableData,
        dateFrom,
        dateTo,
        dataSource,
        timestamp: new Date().toISOString(),
        version: "1.0"
      };
      
      // Сохраняем в S3 под именем dashboard-table.json
      const fileName = "dashboard-table.json";
      const result = await uploadToS3(
        user.email,
        fileName,
        JSON.stringify(dashboardData, null, 2),
        "application/json"
      );
      
      if (result.success) {
        console.log("✅ Таблица Dashboard сохранена в S3");
        debugInfo("Dashboard", "Table saved to S3 successfully");
      }
    } catch (error) {
      console.error("❌ Ошибка при сохранении таблицы в S3:", error);
      debugError("Dashboard", "Error saving table to S3", error);
      // Не показываем ошибку пользователю, так как это фоновая операция
    }
  };

  // Загрузка сохранённой таблицы из S3
  const loadDashboardTableFromS3 = async () => {
    try {
      console.log("📥 Загрузка сохранённой таблицы из S3...");
      debugInfo("Dashboard", "Loading saved table from S3");
      
      // Импортируем S3 сервис
      const { downloadFromS3 } = await import("../services/s3Service");
      
      // Загружаем dashboard-table.json
      const fileName = "dashboard-table.json";
      const result = await downloadFromS3(user.email, fileName);
      
      if (result.success && result.data) {
        const dashboardData = JSON.parse(result.data);
        
        if (dashboardData.tableRows && dashboardData.tableRows.length > 0) {
          // Отладочный вывод первых 3 строк
          console.log('📋 Debug: Первые 3 строки из S3 кэша:');
          dashboardData.tableRows.slice(0, 3).forEach((row, i) => {
            console.log(`  Row #${i}:`, {
              number: row.number,
              date: row.date,
              contractor: row.contractor,
              counterparty: row.counterparty,
              description: row.description,
              amount: row.amount,
              type: row.type
            });
          });
          
          setTableRows(dashboardData.tableRows);
          setDateFrom(dashboardData.dateFrom || yearDates.from);
          setDateTo(dashboardData.dateTo || yearDates.to);
          setDataSource(dashboardData.dataSource || 'cache');
          
          console.log(`✅ Загружено ${dashboardData.tableRows.length} строк из S3`);
          debugInfo("Dashboard", "Table loaded from S3", { 
            rowCount: dashboardData.tableRows.length,
            dateFrom: dashboardData.dateFrom,
            dateTo: dashboardData.dateTo
          });
          
          return true;
        }
      }
      
      console.log("ℹ️ Сохранённая таблица не найдена в S3");
      return false;
    } catch (error) {
      console.error("⚠️ Ошибка при загрузке таблицы из S3:", error);
      debugError("Dashboard", "Error loading table from S3", error);
      return false;
    }
  };

  useEffect(() => {
    console.log("⚡ Dashboard useEffect triggered");
    console.log("🔍 User object in useEffect:", user);
    console.log("🔍 User email:", user?.email);
    console.log("🔍 Data loaded flag:", isDataLoaded);
    
    if (user?.email && !isDataLoaded) {
      // Инициализируем статистику
      setStats({
        totalAccounts: 247,
        activeContracts: 156,
        pendingRequests: 23,
        completedTasks: 89,
      });
      
      // Загружаем сохранённую таблицу из S3
      loadDashboardTableFromS3();
      setIsDataLoaded(true);
      console.log("✅ Dashboard ready, loading saved table from S3");
    } else if (!user?.email) {
      console.warn("⚠️ User not logged in or email missing");
      // Сбрасываем данные при выходе из системы
      setTableRows([]);
      setDataSource('');
      setSelectedRows([]);
      setIsDataLoaded(false);
    }
  }, [user?.email]); // Перезагружаем данные при смене пользователя

  const handleUploadComplete = (response) => {
    debugInfo("Dashboard", "File uploaded successfully", response);
    // Можно добавить уведомление пользователю
  };

  // Синхронизация SBIS данных
  const handleRefreshSbis = async () => {
    debugInfo("Dashboard", "Refreshing SBIS data");
    
    if (!user || !user.email) {
      console.error("❌ User not authenticated:", { user, email: user?.email });
      alert("Ошибка: пользователь не авторизован");
      return;
    }
    
    // Проверяем, выбраны ли даты
    if (!dateFrom || !dateTo) {
      alert("⚠️ Укажите период для синхронизации");
      return;
    }
    
    try {
      setRefreshing(true);
      
      // Импортируем API сервис
      const { syncSbisDocumentsByDate } = await import("../services/sbisApi");
      
      // Вызываем синхронизацию с фильтром по датам
      console.log("📡 Синхронизация документов из SBIS...");
      const result = await syncSbisDocumentsByDate(user.email, dateFrom, dateTo);
      
      debugInfo("Dashboard", "SBIS documents synced successfully", result);
      
      // Формируем детальное сообщение
      const { data } = result;
      
      let message = `✅ СБИС: Синхронизация завершена!\n\n`;
      message += `📅 Период: ${dateFrom} — ${dateTo}\n\n`;
      message += `📊 Статистика:\n`;
      message += `  • Всего документов: ${data.totalDocuments}\n\n`;
      
      if (data.statistics) {
        message += `📋 По типам:\n`;
        for (const [type, stat] of Object.entries(data.statistics)) {
          message += `  • ${stat.description}: ${stat.total}\n`;
        }
      }
      
      if (data.savedFiles && data.savedFiles.length > 0) {
        message += `\n💾 Сохранено файлов: ${data.savedFiles.length}\n`;
      }
      
      console.log(message);
      alert(message);
      
      // После успешной синхронизации загружаем обработанные данные
      await loadInvoiceData();
      
    } catch (error) {
      debugError("Dashboard", "Error syncing SBIS documents", error);
      alert(
        "❌ Ошибка при синхронизации СБИС\n\n" +
        error.message + "\n\n" +
        "Проверьте:\n" +
        "• Настройки SBIS в разделе настроек\n" +
        "• Правильность указанного периода\n" +
        "• Подключение к серверу"
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Парсинг CRD файлов (Турбо)
  const handleRefreshTurbo = async () => {
    debugInfo("Dashboard", "Refreshing Turbo (parsed CRD) data");
    
    if (!user || !user.email) {
      console.error("❌ User not authenticated:", { user, email: user?.email });
      alert("Ошибка: пользователь не авторизован");
      return;
    }
    
    try {
      setRefreshing(true);
      
      console.log("⚡ Парсинг CRD файлов (Турбо)...");
      
      // Вызываем парсинг всех CRD файлов с force=true для принудительного парсинга
      // parseAllCrdFiles(userEmail, format, force)
      const result = await parseAllCrdFiles(user.email, 'json', true); // format='json', force=true
      
      debugInfo("Dashboard", "CRD files parsed successfully", result);
      
      // Формируем сообщение
      let message = `✅ Турбо: Парсинг завершён!\n\n`;
      
      if (result.success) {
        const stats = result.statistics || result.summary || {};
        message += `📊 Статистика:\n`;
        message += `  • Всего файлов: ${stats.filesProcessed || stats.totalFiles || 0}\n`;
        message += `  • Обработано: ${stats.filesSuccess || stats.filesProcessed || 0}\n`;
        message += `  • Пропущено: ${stats.filesSkipped || 0}\n`;
        message += `  • Таблиц извлечено: ${stats.totalTables || stats.tablesExtracted || 0}\n`;
        
        if (result.errors && result.errors.length > 0) {
          message += `\n⚠️ Ошибок: ${result.errors.length}\n`;
        }
      } else {
        message = `❌ Ошибка парсинга CRD файлов\n\n${result.message || 'Неизвестная ошибка'}`;
      }
      
      console.log(message);
      // alert(message); // Убрали модалку после парсинга
      
      // После успешного парсинга загружаем обработанные данные
      await loadInvoiceData();
      
    } catch (error) {
      debugError("Dashboard", "Error parsing CRD files", error);
      alert(
        "❌ Ошибка при парсинге CRD файлов\n\n" +
        error.message + "\n\n" +
        "Проверьте:\n" +
        "• Наличие CRD файлов в хранилище\n" +
        "• Подключение к серверу"
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <button
            className="btn-refresh btn-refresh-sbis"
            onClick={handleRefreshSbis}
            title="Синхронизировать данные из СБИС"
            disabled={refreshing}
          >
            {refreshing ? '⏳' : '📡'} СБИС
          </button>
          <button
            className="btn-refresh btn-refresh-turbo"
            onClick={handleRefreshTurbo}
            title="Парсить CRD файлы (Турбо)"
            disabled={refreshing}
          >
            {refreshing ? '⏳' : '⚡'} Турбо
          </button>
          <button
            className="btn-settings"
            onClick={() => setIsSettingsOpen(true)}
            title="Настройки"
          >
            ⚙️
          </button>
          <button
            className="btn-upload"
            onClick={() => setIsUploadOpen(true)}
            title="Загрузить файл"
          >
            📤
          </button>
          <div className="date-filter">
            <label>
              Дата с:
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
              />
            </label>
            <label>
              Дата по:
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
              />
            </label>
            <button className="btn-filter" onClick={handleFilter}>
              Фильтровать
            </button>
          </div>
        </div>
        {onLogout && (
          <button className="btn btn-logout" onClick={onLogout}>
            Выйти
          </button>
        )}
      </div>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <FileUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUploadComplete={handleUploadComplete}
      />

      {/* Таблица с чекбоксами и нужными столбцами */}
        <div className="dashboard-table-section">
          <div className="table-header">
            <div className="table-search">
              <input
                type="text"
                placeholder="Поиск по номеру или контрагенту..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
            <h2>Документы</h2>
          </div>
          
          {/* Локальный индикатор загрузки таблицы */}
          {refreshing && (
            <div className="table-loading-overlay">
              <div className="loading-spinner"></div>
              <p>Обновление данных...</p>
            </div>
          )}
          
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredTableRows.length && filteredTableRows.length > 0}
                    onChange={handleSelectAll}
                    aria-label="Выделить все"
                  />
                </th>
                <th title="Текст (из parsed файлов)">Т</th>
                <th title="СБИС (из СБИС API)">С</th>
                <th>Номер</th>
                <th 
                  className={`sortable ${sortConfig.key === 'date' ? 'sorted' : ''}`}
                  onClick={() => handleSort('date')}
                >
                  Дата
                  {sortConfig.key === 'date' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th 
                  className={`sortable ${sortConfig.key === 'contractor' ? 'sorted' : ''}`}
                  onClick={() => handleSort('contractor')}
                >
                  Контрагент
                  {sortConfig.key === 'contractor' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th 
                  className={`sortable ${sortConfig.key === 'amount' ? 'sorted' : ''}`}
                  onClick={() => handleSort('amount')}
                >
                  Сумма
                  {sortConfig.key === 'amount' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}
                    </span>
                  )}
                </th>
                <th>Тип</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {filteredTableRows.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    {tableRows.length === 0 ? (
                      <div>
                        <p style={{ fontSize: '16px', marginBottom: '10px' }}>📋 Нет данных для отображения</p>
                        <p style={{ fontSize: '14px' }}>Нажмите кнопку "🔄 Обновить" для загрузки документов из СБИС</p>
                      </div>
                    ) : (
                      <p>Ничего не найдено по запросу "{searchQuery}"</p>
                    )}
                  </td>
                </tr>
              ) : (
                filteredTableRows.map((row) => {
                  // Определяем, есть ли данные из разных источников
                  const hasParsed = row.source === 'parsed' || row.hasParsed || 
                                   (row.sources && row.sources.includes('parsed')) ||
                                   (row.sources && row.sources.includes('crd'));
                  const hasSbis = row.source === 'sbis' || row.hasSbis || 
                                 (row.sources && row.sources.includes('sbis'));
                  const sbisLink = row.sbisLink || '';
                  
                  return (
                    <tr key={row.id} className={selectedRows.includes(row.id) ? "selected" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(row.id)}
                          onChange={() => handleSelectRow(row.id)}
                          aria-label={`Выделить строку ${row.id}`}
                        />
                      </td>
                      <td style={{ textAlign: 'center', backgroundColor: '#d4edda' }} title={hasParsed ? 'Данные из Турбо (CRD)' : ''}>
                        {hasParsed ? (
                          <span 
                            style={{ cursor: 'pointer' }} 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateInvoiceClick(row);
                            }}
                            title="Показать данные из CRD"
                          >
                            ✓
                          </span>
                        ) : ''}
                      </td>
                      <td style={{ textAlign: 'center', backgroundColor: '#d1ecf1', padding: '4px' }} title={hasSbis ? 'Данные из СБИС' : ''}>
                        {hasSbis && sbisLink ? (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                            <a 
                              href={sbisLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ textDecoration: 'none', fontSize: '16px', cursor: 'pointer', display: 'inline-block', width: '20px', textAlign: 'center' }}
                              title="Открыть документ в СБИС"
                              onClick={(e) => e.stopPropagation()}
                            >
                              🔗
                            </a>
                            <span style={{ display: 'inline-block', width: '20px', textAlign: 'center', fontSize: '14px', cursor: 'default' }} title={row.processed ? "Проведено в СБИС" : ""}>
                              {row.processed ? '✅' : ''}
                            </span>
                          </div>
                        ) : hasSbis ? '✓' : hasParsed ? (
                          <span 
                            style={{ fontSize: '18px', cursor: 'pointer', display: 'inline-block' }} 
                            title="Создать СФ в СБИС"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateInvoiceClick(row);
                            }}
                          >
                            ➕
                          </span>
                        ) : ''}
                      </td>
                      <td>{row.number || row.id}</td>
                      <td>{row.date}</td>
                      <td>{row.contractor}</td>
                      <td>{row.amount.toLocaleString("ru-RU")}</td>
                      <td>{row.type}</td>
                      <td>{row.status}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Модальное окно с данными CRD */}
        {isCrdModalOpen && selectedCrdData && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}
            onClick={() => setIsCrdModalOpen(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '90%',
                width: '1000px',
                maxHeight: '90vh',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Заголовок */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '20px 20px 15px 20px',
                borderBottom: '1px solid #e0e0e0'
              }}>
                <h2 style={{ margin: 0 }}>
                  Данные из CRD файла
                  {selectedCrdData.tableName && (
                    <span style={{ fontSize: '14px', color: '#666', marginLeft: '10px' }}>
                      ({selectedCrdData.tableName}{selectedCrdData.yearMonth ? ` - ${selectedCrdData.yearMonth}` : ''})
                    </span>
                  )}
                </h2>
                <button 
                  onClick={() => setIsCrdModalOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Прокручиваемый контент */}
              <div style={{
                flex: '1',
                overflow: 'auto',
                padding: '20px'
              }}>

              {selectedCrdData.loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
                  <p>Загрузка данных из CRD...</p>
                </div>
              ) : selectedCrdData.error ? (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  color: '#721c24', 
                  padding: '20px', 
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: '0 0 10px 0' }}>❌ Ошибка загрузки данных</h3>
                  <p style={{ margin: 0 }}>{selectedCrdData.error}</p>
                </div>
              ) : selectedCrdData.crdData ? (
                <div>
                  {/* JSON отображение данных с подсветкой синтаксиса */}
                  <pre style={{
                    backgroundColor: '#1e1e1e',
                    padding: '20px',
                    borderRadius: '4px',
                    border: '1px solid #333',
                    overflow: 'auto',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    margin: 0,
                    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                    whiteSpace: 'pre',
                    textAlign: 'left'
                  }}>
                    <code style={{ display: 'block' }} dangerouslySetInnerHTML={{
                      __html: (() => {
                        const json = JSON.stringify(selectedCrdData.crdData, null, 2);
                        return json
                          .replace(/&/g, '&amp;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                          .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
                            let cls = 'number';
                            if (/^"/.test(match)) {
                              if (/:$/.test(match)) {
                                cls = 'key';
                              } else {
                                cls = 'string';
                              }
                            } else if (/true|false/.test(match)) {
                              cls = 'boolean';
                            } else if (/null/.test(match)) {
                              cls = 'null';
                            }
                            const colors = {
                              key: '#9cdcfe',
                              string: '#ce9178',
                              number: '#b5cea8',
                              boolean: '#569cd6',
                              null: '#569cd6'
                            };
                            return `<span style="color: ${colors[cls]}">${match}</span>`;
                          })
                          .replace(/([{}\[\],])/g, '<span style="color: #d4d4d4">$1</span>');
                      })()
                    }} />
                  </pre>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>Нет данных для отображения</p>
                </div>
              )}
              </div>

              {/* Закрепленный футер с кнопками */}
              <div style={{ 
                borderTop: '1px solid #e0e0e0',
                padding: '15px 20px',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '10px',
                backgroundColor: '#f8f9fa'
              }}>
                <button
                  onClick={() => setIsCrdModalOpen(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Закрыть
                </button>
                {selectedCrdData.crdData && !selectedCrdData.loading && !selectedCrdData.error && (() => {
                  // Определяем, есть ли документ в СБИС
                  const hasSbis = selectedCrdData.source === 'sbis' || 
                                 selectedCrdData.hasSbis || 
                                 (selectedCrdData.sources && selectedCrdData.sources.includes('sbis'));
                  const sbisLink = selectedCrdData.sbisLink || '';
                  
                  if (hasSbis && sbisLink) {
                    // Если документ есть в СБИС и есть ссылка - показываем кнопку "Открыть в СБИС"
                    return (
                      <button
                        onClick={() => {
                          window.open(sbisLink, '_blank', 'noopener,noreferrer');
                        }}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        title="Открыть документ в СБИС"
                      >
                        🔗 Открыть в СБИС
                      </button>
                    );
                  } else {
                    // Если документа нет в СБИС - показываем кнопку "Создать в СБИС"
                    return (
                      <button
                        onClick={() => handleCreateSbisDocument(selectedCrdData)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        title="Создать документ в СБИС с XML вложением"
                      >
                        ➕ Создать в СБИС
                      </button>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default Dashboard;
